import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
    CheckCircle2, 
    Sparkles, 
    Mail, 
    ArrowRight, 
    Download, 
    ShieldCheck, 
    Zap, 
    FileText, 
    Smile, 
    AlertCircle,
    Loader2,
    Calendar,
    ChevronRight,
    Eye
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const getApiUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:8001/api';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8001/api';
    }
    return `${window.location.origin}/api`;
};
const API = getApiUrl();

const DEFAULT_PREVIEW_PAGES = [
    { id: 'cover', title: 'Issue Cover', label: 'Front Cover', src: '/magazine_previews/cover.webp', desc: '11-page printable weekly chronicle with retro newsprint aesthetic' },
    { id: 'p04', title: 'Silicon Satire', label: 'Page 4', src: '/magazine_previews/page_04.webp', desc: 'Original 4-panel tech satire exploring everyday developer quirks' },
    { id: 'p06', title: 'Hardware at Home', label: 'Page 6', src: '/magazine_previews/page_06.webp', desc: 'Desktop computing breakthroughs explained with plain-English wit' },
    { id: 'p10', title: 'Math Breakthrough', label: 'Page 10', src: '/magazine_previews/page_10.webp', desc: 'University-level AI problem solving grounded in verified numbers' },
];

export default function SubscribePage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [bottomEmail, setBottomEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('idle'); // 'idle' | 'success' | 'already' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [activePreview, setActivePreview] = useState(0);
    const [latestMag, setLatestMag] = useState(null);
    const [subscriberToken, setSubscriberToken] = useState('');
    const [previewPages, setPreviewPages] = useState(DEFAULT_PREVIEW_PAGES);

    useEffect(() => {
        document.title = "Get the Free AI Comic Magazine | The Neural Feed";
        window.scrollTo(0, 0);

        // Fetch latest weekly comic issue dynamically
        axios.get(`${API}/magazines/latest`)
            .then(res => {
                if (res.data?.toons) {
                    setLatestMag(res.data.toons);
                }
            })
            .catch(err => {
                console.log('Using default magazine issue:', err);
            });

        // Check if dynamic weekly previews manifest exists
        axios.get('/magazine_previews/previews.json')
            .then(res => {
                if (Array.isArray(res.data?.pages) && res.data.pages.length > 0) {
                    setPreviewPages(res.data.pages);
                }
            })
            .catch(() => {
                // Fallback to default preview pages
            });
    }, []);

    const getDownloadUrl = () => {
        if (latestMag?.is_public_sample) {
            return latestMag.public_download_url || `https://theneuralfeed.com/magazines/${latestMag.pdf_filename || "The_Neural_Feed_Illustrated_Issue_06.pdf"}`;
        }
        if (subscriberToken) {
            const base = latestMag?.public_download_url || `https://theneuralfeed.com/magazines/${latestMag?.pdf_filename || "The_Neural_Feed_Illustrated_Issue_06.pdf"}`;
            return `${base}?token=${subscriberToken}`;
        }
        const magId = latestMag?.id || "magazine-toons-issue-06";
        return `/magazine/${magId}`;
    };

    useEffect(() => {
        // Capture UTM parameters from URL search query
        const queryParams = new URLSearchParams(window.location.search);
        const utmSource = queryParams.get('utm_source');
        const utmMedium = queryParams.get('utm_medium');
        const utmCampaign = queryParams.get('utm_campaign');

        if (utmSource) {
            sessionStorage.setItem('tnf_utm_source', utmSource);
            if (utmMedium) sessionStorage.setItem('tnf_utm_medium', utmMedium);
            if (utmCampaign) sessionStorage.setItem('tnf_utm_campaign', utmCampaign);

            // Register with PostHog
            if (window.posthog) {
                window.posthog.register({
                    utm_source: utmSource,
                    utm_medium: utmMedium || 'social',
                    utm_campaign: utmCampaign || 'none',
                });
                window.posthog.capture('subscribe_visit', {
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                });
            }

            // Record in Google Analytics
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'page_view', {
                    page_path: window.location.pathname + window.location.search,
                    campaign_source: utmSource,
                    campaign_medium: utmMedium,
                    campaign_name: utmCampaign,
                });
            }
        }
    }, []);

    const handleSubscribe = async (inputEmail) => {
        const cleanEmail = (inputEmail || '').trim().toLowerCase();
        if (!cleanEmail || !cleanEmail.includes('@')) {
            setErrorMessage('Please enter a valid email address.');
            setStatus('error');
            return;
        }

        setLoading(true);
        setStatus('idle');
        setErrorMessage('');

        const queryParams = new URLSearchParams(window.location.search);
        const utmSource = queryParams.get('utm_source') || sessionStorage.getItem('tnf_utm_source') || 'direct';
        const utmMedium = queryParams.get('utm_medium') || sessionStorage.getItem('tnf_utm_medium') || 'web';
        const utmCampaign = queryParams.get('utm_campaign') || sessionStorage.getItem('tnf_utm_campaign') || 'none';

        try {
            const res = await axios.post(`${API}/subscribers`, { 
                email: cleanEmail,
                utm_source: utmSource,
                utm_medium: utmMedium,
                utm_campaign: utmCampaign
            });
            localStorage.setItem('tnf_subscribed', 'true');
            setStatus('success');

            // Fire signup event to Google Analytics
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'signup', {
                    method: 'comic_magazine_page',
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                });
            }

            // Fire signup event to PostHog
            if (window.posthog) {
                window.posthog.capture('signup', {
                    method: 'comic_magazine_page',
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                });
            }
        } catch (err) {
            console.error('Subscription error:', err);
            const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
            if (msg.toLowerCase().includes('already')) {
                setStatus('already');
            } else {
                setStatus('error');
                setErrorMessage(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col justify-between selection:bg-amber-500/30 selection:text-amber-200">
            {/* Top Header */}
            <header className="border-b border-slate-800/80 bg-[#0B1120]/90 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src="/logo192.png"
                            alt="The Neural Feed"
                            className="w-9 h-9 rounded-lg object-cover border border-emerald-500/30 shadow-sm transition-transform group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className="font-black tracking-tight text-base text-white group-hover:text-amber-400 transition-colors">
                                THE NEURAL FEED
                            </span>
                            <span className="text-[10px] font-mono tracking-wider text-slate-400 uppercase -mt-0.5">
                                Comics & AI Intelligence
                            </span>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/issues"
                            className="hidden sm:inline-flex items-center text-xs font-semibold text-slate-300 hover:text-amber-400 transition-colors"
                        >
                            Past Issues
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-full text-xs font-semibold"
                        >
                            Browse Today's News <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Page Content */}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-10 md:py-14 w-full flex flex-col items-center">
                
                {/* 1. Hero Headline — Leading with the Comic */}
                <div className="text-center max-w-2xl mx-auto mb-8">
                    <Badge variant="outline" className="mb-4 py-1 px-3 border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-full text-xs font-mono font-semibold tracking-wide">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5 inline text-amber-400" />
                        FREE WEEKLY AI COMIC MAGAZINE
                    </Badge>
                    
                    <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight mb-4 font-['Chivo',sans-serif]">
                        Get the free AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">comic magazine.</span>
                    </h1>
                    
                    <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mx-auto">
                        Real AI breakthroughs meet vintage newspaper satire. Delivered every Monday morning, with a 5-minute news digest on weekdays to keep you ahead.
                    </p>
                </div>

                {/* 2. Top Signup Card */}
                <div id="top-signup" className="w-full max-w-xl mx-auto mb-14">
                    <Card className="border border-slate-700/80 bg-gradient-to-b from-[#0F172A] to-[#0B1120] shadow-2xl rounded-2xl overflow-hidden relative">
                        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-emerald-400 to-teal-400" />

                        <CardContent className="p-6 sm:p-8">
                            {status === 'idle' || status === 'error' ? (
                                <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(email); }} className="space-y-4">
                                    <div className="space-y-2">
                                        <label htmlFor="top-email" className="block text-xs font-mono font-bold tracking-wider uppercase text-slate-300">
                                            Where should we send your copy?
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                id="top-email"
                                                type="email"
                                                required
                                                autoFocus
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                className="pl-10 h-12 bg-slate-900/90 border-slate-700 text-white placeholder:text-slate-500 text-base rounded-xl focus-visible:ring-amber-500"
                                            />
                                        </div>
                                    </div>

                                    {status === 'error' && errorMessage && (
                                        <div className="p-3 bg-red-950/40 border border-red-800/60 rounded-lg text-red-300 text-xs flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                            <span>{errorMessage}</span>
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-12 text-sm sm:text-base font-bold bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 rounded-xl shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                                Preparing Your Comic...
                                            </>
                                        ) : (
                                            <>
                                                Get this week's comic →
                                            </>
                                        )}
                                    </Button>

                                    <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
                                        <span className="flex items-center gap-1.5">
                                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                            Instant download + email delivery
                                        </span>
                                        <span>100% Free • Unsubscribe anytime</span>
                                    </div>
                                </form>
                            ) : (
                                /* Success State */
                                <div className="text-center py-4 space-y-5 animate-in fade-in duration-300">
                                    <div className="w-14 h-14 bg-emerald-500/20 border border-emerald-500/40 rounded-full flex items-center justify-center mx-auto text-emerald-400 shadow-inner">
                                        <CheckCircle2 className="w-8 h-8" />
                                    </div>

                                    <div className="space-y-1">
                                        <h2 className="text-2xl font-black text-white">
                                            {status === 'already' ? "You're Already on the List!" : "🎉 You're in! Welcome!"}
                                        </h2>
                                        <p className="text-sm text-slate-300 max-w-sm mx-auto">
                                            {status === 'already' 
                                                ? "Your subscription is active! Check your inbox for your direct access link."
                                                : "We've sent this week's 11-page comic edition to your inbox! Check your email to confirm and read."}
                                        </p>
                                    </div>

                                    {/* Direct PDF Download Action */}
                                    <div className="p-4 bg-slate-900/90 border border-amber-500/40 rounded-xl text-left space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-mono font-bold tracking-wider uppercase bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">
                                                {subscriberToken ? "🎁 READY TO READ" : "📬 ISSUE DISPATCHED"}
                                            </span>
                                            <span className="text-xs text-slate-400">11 Pages • Printable PDF</span>
                                        </div>
                                        <p className="text-sm font-bold text-white leading-snug">
                                            The Neural Feed Illustrated — {latestMag?.period_label ? `Edition (${latestMag.period_label})` : "This Week's Full Edition"}
                                        </p>
                                        {subscriberToken ? (
                                            <a
                                                href={getDownloadUrl()}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center justify-center w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-lg transition-colors gap-2 shadow-md hover:shadow-amber-500/20"
                                            >
                                                <Download className="w-4 h-4" /> Download this week's comic (PDF)
                                            </a>
                                        ) : (
                                            <div className="text-xs text-slate-300 bg-slate-800/80 p-3 rounded-lg border border-slate-700/80 flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                                <span>Check your inbox for your welcome email containing your comic PDF and access link!</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="pt-2">
                                        <Button
                                            onClick={() => navigate('/')}
                                            variant="outline"
                                            className="w-full h-11 border-slate-700 hover:bg-slate-800 text-slate-200 text-xs font-semibold rounded-xl"
                                        >
                                            Browse Today's AI News Feed <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* 3. Real Cover & Interior Page Previews Gallery */}
                <div className="w-full max-w-3xl mb-16">
                    <div className="text-center mb-6">
                        <h2 className="text-xs font-mono font-bold tracking-widest text-slate-400 uppercase">
                            TAKE A PEEK INSIDE THIS WEEK'S ISSUE
                        </h2>
                        <p className="text-sm text-slate-300 mt-1">
                            Click any page to preview the art, jokes, and simplified tech breakdowns.
                        </p>
                    </div>

                    {/* Preview Tabs / Thumbnails */}
                    <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
                        {previewPages.map((page, idx) => (
                            <button
                                key={page.id || idx}
                                onClick={() => setActivePreview(idx)}
                                className={`py-1.5 px-3 rounded-full text-xs font-medium transition-all ${
                                    activePreview === idx
                                        ? 'bg-amber-500 text-slate-950 font-bold shadow-md'
                                        : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white'
                                }`}
                            >
                                {page.label} • {page.title}
                            </button>
                        ))}
                    </div>

                    {/* Featured Preview Viewer */}
                    {(() => {
                        const cur = previewPages[activePreview] || previewPages[0] || DEFAULT_PREVIEW_PAGES[0];
                        return (
                            <div className="p-4 sm:p-6 bg-[#0B1120] border border-slate-800 rounded-2xl shadow-2xl flex flex-col md:flex-row items-center gap-6">
                                <div className="w-full md:w-1/2 aspect-[9/12.7] rounded-xl overflow-hidden border border-slate-700/80 shadow-lg bg-slate-950 flex items-center justify-center relative group">
                                    <img
                                        src={cur.src}
                                        alt={cur.title}
                                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                                        loading="lazy"
                                    />
                                    <div className="absolute bottom-2 right-2 bg-slate-950/80 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-mono text-slate-300 border border-slate-700/50">
                                        🔍 Real Issue Page
                                    </div>
                                </div>

                                <div className="w-full md:w-1/2 space-y-4 text-center md:text-left">
                                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[11px] font-mono">
                                        {cur.label}: {cur.title}
                                    </Badge>

                                    <h3 className="text-xl font-bold text-white leading-snug">
                                        {cur.desc}
                                    </h3>

                            <p className="text-xs text-slate-300 leading-relaxed">
                                Every Monday, we distill the week's biggest AI breakthroughs into full-color, 4-panel newspaper comics with sharp dialogue and verified metrics. No dry jargon—just smart humor for curious minds.
                            </p>

                            <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800 text-xs text-slate-400 space-y-1.5">
                                <div className="flex items-center gap-2 text-slate-200 font-semibold">
                                    <Smile className="w-4 h-4 text-amber-400 shrink-0" />
                                    <span>What's inside each weekly issue:</span>
                                </div>
                                <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1">
                                    <li>8 original 4-panel comic stories</li>
                                    <li>Executive tech satire & company parodies</li>
                                    <li>Printable 11-page high-resolution PDF</li>
                                </ul>
                            </div>

                            <a
                                href="#top-signup"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById('top-email')?.focus();
                                    window.scrollTo({ top: 120, behavior: 'smooth' });
                                }}
                                className="inline-flex items-center text-xs font-bold text-amber-400 hover:text-amber-300 transition-colors gap-1 pt-1"
                            >
                                Subscribe to read the full issue <ChevronRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                );
            })()}
        </div>

                {/* 4. Clear Breakdown: What You Get & When */}
                <div className="w-full max-w-3xl mb-16">
                    <h2 className="text-xs font-mono font-bold tracking-widest text-slate-400 uppercase text-center mb-6">
                        HOW YOUR SUBSCRIPTION WORKS
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Monday Drop */}
                        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1E1B4B]/30 to-[#0F172A] border border-amber-500/30 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/20 text-amber-300 uppercase">
                                    EVERY MONDAY MORNING
                                </span>
                                <Smile className="w-5 h-5 text-amber-400" />
                            </div>
                            <h3 className="font-bold text-base text-white">The Neural Feed Illustrated</h3>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                Start your week with our 11-page printable comic magazine. Smart satire, character-driven parodies, and easy-to-understand breakdowns of the biggest AI stories.
                            </p>
                        </div>

                        {/* Weekday Brief */}
                        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#064E3B]/20 to-[#0F172A] border border-emerald-500/30 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-400 uppercase">
                                    WEEKDAY MORNINGS
                                </span>
                                <Zap className="w-5 h-5 text-emerald-400" />
                            </div>
                            <h3 className="font-bold text-base text-white">5-Minute AI News Digest</h3>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                Top 10 vetted stories scanned from 165+ developer feeds and research preprints, paired with daily visual Neural Snap cards so you stay informed in minutes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* 5. Second Signup Box at Bottom */}
                <div className="w-full max-w-xl mx-auto mb-10">
                    <Card className="border border-slate-700/80 bg-gradient-to-b from-[#0F172A] to-[#070B14] shadow-2xl rounded-2xl overflow-hidden relative">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-400" />

                        <CardContent className="p-6 sm:p-8 text-center space-y-4">
                            <h3 className="text-xl sm:text-2xl font-black text-white font-['Chivo',sans-serif]">
                                Ready to read this week's comic?
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-300 max-w-md mx-auto">
                                Enter your email below to get instant access right on screen and in your inbox.
                            </p>

                            <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(bottomEmail); }} className="space-y-3 text-left">
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="email"
                                        required
                                        value={bottomEmail}
                                        onChange={(e) => setBottomEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="pl-10 h-12 bg-slate-900/90 border-slate-700 text-white placeholder:text-slate-500 text-base rounded-xl focus-visible:ring-amber-500"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-12 text-sm sm:text-base font-bold bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 rounded-xl shadow-lg transition-all"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            Get this week's comic →
                                        </>
                                    )}
                                </Button>
                            </form>

                            <p className="text-[11px] text-slate-400">
                                🔒 100% Free • Delivered every Monday • Unsubscribe anytime with 1 click
                            </p>
                        </CardContent>
                    </Card>
                </div>

            </main>

            {/* Footer */}
            <footer className="border-t border-slate-800/80 py-8 text-center text-xs text-slate-400 space-y-2">
                <p>© 2026 The Neural Feed. Independent AI Intelligence & Visual Comics.</p>
                <div className="flex items-center justify-center gap-4 text-slate-400">
                    <Link to="/" className="hover:text-slate-200 transition-colors">Daily News Feed</Link>
                    <span>•</span>
                    <Link to="/issues" className="hover:text-slate-200 transition-colors">Past Issues</Link>
                    <span>•</span>
                    <Link to="/privacy" className="hover:text-slate-200 transition-colors">Privacy Policy</Link>
                    <span>•</span>
                    <a href="mailto:contact@theneuralfeed.com" className="hover:text-slate-200 transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}
